import axios, { AxiosRequestConfig, ResponseType, AxiosError } from "axios";
import config from "@/config";
import { getTempUserToken, getUserToken } from "./storage-data";
import log from "@/utils/logger";
import { addRequestToSession } from "@/utils/sessionManager";
import { refreshAccessToken } from "./refresh-token";
import { getLocalDomainHeader } from "@/utils/local-domain";
// import { clearStorage } from './storage'

// TEMPORARY: Stop all /api/tickets network calls during testing.
// Set to false to re-enable requests.
const TEMP_DISABLE_TICKETS_ENDPOINTS = false;

const isNextProductionBuild = () =>
  process.env.NEXT_PHASE === "phase-production-build";

const apiRequest = async (
  method = "GET",
  url: string,
  data: any = {},
  language?: string,
  requestAbortController?: AbortController,
  responseType: ResponseType = undefined
) => {
  const methodName = `${method || "GET"}`.toUpperCase();

  if (isNextProductionBuild()) {
    return {
      success: true,
      status: 200,
      message: "Skipped during next build",
      data: null,
    };
  }

  try {
    const normalizedUrl = typeof url === "string" ? url : "";
    const isTicketsEndpoint =
      normalizedUrl === "/api/tickets" ||
      normalizedUrl.startsWith("/api/tickets/") ||
      normalizedUrl.startsWith("/api/tickets?");

    if (TEMP_DISABLE_TICKETS_ENDPOINTS && isTicketsEndpoint) {
      const upperMethod = `${method || "GET"}`.toUpperCase();
      let mockedData: any = {};

      if (
        normalizedUrl === "/api/tickets" ||
        normalizedUrl.startsWith("/api/tickets?")
      ) {
        mockedData = [];
      } else if (normalizedUrl.endsWith("/unread-count")) {
        mockedData = { unreadCount: 0 };
      } else if (normalizedUrl.endsWith("/unread-ids")) {
        mockedData = { ticketIds: [] };
      } else if (normalizedUrl.endsWith("/first-unread")) {
        mockedData = { ticketId: null };
      } else if (upperMethod === "GET") {
        mockedData = [];
      }

      return {
        success: true,
        status: 200,
        message: "Temporarily disabled /api/tickets",
        data: mockedData,
      };
    }

    //please don't change the server url!!!
    const SERVER = config.api_server;
    const reqUrl = SERVER + url;
    const controller = new AbortController();
    let userToken = getUserToken();
    if (!userToken) {
      userToken = getTempUserToken();
    }

    // Don't add Authorization header for refresh token endpoint (it uses refresh token in body)
    const isRefreshEndpoint = url.includes("/v1/auth/refresh-token");

    const options: AxiosRequestConfig = {
      method: methodName,
      url: reqUrl,
      responseType: responseType ?? "json",
      signal: requestAbortController?.signal,
      // Prevent infinite global loading lock when ERP/CRM hangs
      timeout: 60000,
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "project-name": "business-dashboard",
        ...(!isRefreshEndpoint &&
          userToken && { Authorization: "Bearer " + userToken }),
        ...(data.customAuth && { "auth-token": data.customAuth }),
        lang: "en",
        domain: getLocalDomainHeader(),
      },
    };
    if (methodName === "GET") {
      options.params = {
        ...data,
      };
    } else {
      options.data = data;
    }

    // log.info("Starting Request", options);
    addRequestToSession({ method, url, data, options });

    const response = await axios(options);
    // log.info("Response:", response);
    addRequestToSession({ method, url, data, options, response });

    if (response) {
      return {
        success: true,
        status: response?.status,
        message: response?.data?.message,
        data: response?.data,
      };
    }
  } catch (err: any) {
    const axiosError = err as AxiosError;

    // Handle 401 Unauthorized - try to refresh token
    // Always call refreshAccessToken on 401: it dedupes concurrent callers — waiters
    // are queued until the in-flight refresh finishes, then all receive the same token.
    // Skip refresh if this is the refresh token endpoint itself (avoids infinite loop).
    const isRefreshEndpoint = url.includes("/v1/auth/refresh-token");
    if (
      axiosError.response?.status === 401 &&
      getUserToken() &&
      !isRefreshEndpoint
    ) {
      try {
        // Attempt to refresh the token
        const newToken = await refreshAccessToken();

        if (newToken) {
          // Reconstruct request options for retry
          const SERVER = config.api_server;
          const reqUrl = SERVER + url;
          const retryOptions: AxiosRequestConfig = {
            method: methodName,
            url: reqUrl,
            data: methodName !== "GET" ? data : undefined,
            params: methodName === "GET" ? data : undefined,
            responseType: responseType ?? "json",
            signal: requestAbortController?.signal,
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
              "project-name": "business-dashboard",
              Authorization: "Bearer " + newToken,
              ...(data.customAuth && { "auth-token": data.customAuth }),
              lang: "en",
              domain: getLocalDomainHeader(),
            },
          };

          const retryResponse = await axios(retryOptions);
          addRequestToSession({
            method,
            url,
            data,
            options: retryOptions,
            response: retryResponse,
          });

          return {
            success: true,
            status: retryResponse?.status,
            message: retryResponse?.data?.message,
            data: retryResponse?.data,
          };
        }
      } catch (refreshError) {
        log.error("Token refresh failed", refreshError);
        // If refresh fails, continue to return the original error
      }
    }

    log.error("Request Error", err);
    addRequestToSession({ method, url, data, error: err });

    return {
      success: false,
      status: err?.response?.status,
      message: err?.response?.data?.error?.ErrorMessage,
      errors: err?.response?.data?.error,
      msg: err?.response?.data?.message,
      statusCode: err?.response?.data?.error?.statusCode,
      data: err?.response?.data,
    };
  }
};

export const getApiRequest = async (url: string, data: any) => {
  return await apiRequest("GET", url, data);
};
export const postApiRequest = async (url: string, data: any) => {
  return await apiRequest("POST", url, data);
};
export const putApiRequest = async (url: string, data: any) => {
  return await apiRequest("PUT", url, data);
};
export const patchApiRequest = async (url: string, data: any) => {
  return await apiRequest("PATCH", url, data);
};
export const deleteApiRequest = async (url: string, data: any = {}) => {
  return await apiRequest("DELETE", url, data);
};

export default apiRequest;
